import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FreelancerBreakpoints,
  FreelancerMaxBreakpoint,
} from '@freelancer/ui/breakpoints';
import type { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs';

/**
 * Service for detecting and observing screen size changes.
 * Returns mutually exclusive results for mobile, tablet, and desktop sizes.
 *
 * IMPORTANT: This service is designed for client-side use only.
 *
 * This is safe to use if the feature will not be ever rendered by the server.
 *
 * SSR doesn't have access to the screen size so this service should not be used during SSR.
 */
@Injectable({
  providedIn: 'root',
})
export class ScreenSizeService {
  private breakpointObserver = inject(BreakpointObserver);
  private platformId = inject(PLATFORM_ID);

  private observer$ = this.breakpointObserver
    .observe([
      FreelancerBreakpoints.MOBILE_SMALL,
      FreelancerBreakpoints.MOBILE,
      FreelancerMaxBreakpoint.TABLET,
      FreelancerMaxBreakpoint.TABLET_LANDSCAPE,
      FreelancerBreakpoints.TABLET,
      FreelancerBreakpoints.DESKTOP_SMALL,
      FreelancerBreakpoints.DESKTOP_XLARGE,
      FreelancerBreakpoints.DESKTOP_XXLARGE,
    ])
    .pipe(
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  /**
   * Returns true if the screen size is exactly mobile xsmall.
   */
  isMobileXSmall(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(result => !result.breakpoints[FreelancerBreakpoints.MOBILE_SMALL]),
    );
  }

  /**
   * Returns true if the screen size is exactly mobile.
   */
  isMobileSmall(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(result => !result.breakpoints[FreelancerBreakpoints.MOBILE]),
    );
  }

  /**
   * Returns true if the screen size is mobile.
   */
  isMobile(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(result => result.breakpoints[FreelancerMaxBreakpoint.TABLET]),
    );
  }

  /**
   * Returns true if the screen size is mobile on landscape.
   */
  isMobileLandscape(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(result => {
        return result.breakpoints[FreelancerMaxBreakpoint.TABLET_LANDSCAPE];
      }),
    );
  }

  /**
   * Returns true if the screen size is exactly tablet.
   */
  isTablet(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(
        result =>
          result.breakpoints[FreelancerBreakpoints.TABLET] &&
          !result.breakpoints[FreelancerBreakpoints.DESKTOP_SMALL],
      ),
    );
  }

  /**
   * Returns true if the screen size is from desktop small to desktop large.
   */
  isDesktopSmall(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(
        result =>
          result.breakpoints[FreelancerBreakpoints.DESKTOP_SMALL] &&
          !result.breakpoints[FreelancerBreakpoints.DESKTOP_XLARGE],
      ),
    );
  }

  /**
   * Returns true if the screen size is exactly desktop xlarge.
   */
  isDesktopXLarge(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(
        result =>
          result.breakpoints[FreelancerBreakpoints.DESKTOP_XLARGE] &&
          !result.breakpoints[FreelancerBreakpoints.DESKTOP_XXLARGE],
      ),
    );
  }

  /**
   * Returns true if the screen size is exactly desktop xxlarge.
   */
  isDesktopXXLarge(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(result => result.breakpoints[FreelancerBreakpoints.DESKTOP_XXLARGE]),
    );
  }

  /**
   * Returns true if the screen size is desktop.
   * That is any size larger than desktop small.
   */
  isDesktop(): Observable<boolean> {
    this.checkPlatform();

    return this.observer$.pipe(
      map(result => result.breakpoints[FreelancerBreakpoints.DESKTOP_SMALL]),
    );
  }

  isDesktopSignal(): Signal<boolean> {
    return toSignal(this.isDesktop(), { requireSync: true });
  }

  isTabletSignal(): Signal<boolean> {
    return toSignal(this.isTablet(), { requireSync: true });
  }

  isMobileSignal(): Signal<boolean> {
    return toSignal(this.isMobile(), { requireSync: true });
  }

  private checkPlatform(): void {
    if (!this.isSupported()) {
      throw new Error('ScreenSizeService should not be used during SSR.');
    }
  }

  private isSupported(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
